/**
 * The bass patches. Plain data objects — "the knobs ARE the patch": the engine
 * reads a live params object at note time, so a gesture that moves cutoff moves
 * the next note without rebuilding anything.
 *
 * Transferred from Chirp/bittern.html — the CALLS table, reduced to the two
 * calls this instrument ships (ACID and PSY) plus their step/slide/degree data.
 */

export interface BassParams {
  cutoff: number;   // Hz
  res: number;      // Q on the first pole
  envAmt: number;   // filter envelope depth, in octaves x 3.6
  fdec: number;     // filter decay, seconds
  a: number; d: number; s: number; r: number;
  penv: number;     // pitch envelope, semitones
  ptime: number;
  glide: number;    // seconds
  accentAmt: number;
  drive: number;
  unison: number;
  detune: number;   // cents
  sub: 0 | 1 | 2;
}

export interface BassPatch {
  readonly label: string;
  readonly note: string;
  readonly bpm: number;
  readonly scale: 'minpent' | 'phrygian' | 'minor';
  readonly gate: number;
  readonly mask: readonly boolean[];
  readonly slide: readonly boolean[];
  readonly accent: readonly boolean[];
  readonly deg: readonly number[];
  readonly params: BassParams;
}

const bits = (s: string): boolean[] => s.split('').map((c) => c === '1');

export const BASS_PATCHES = {
  acid: {
    label: 'ACID',
    note: 'Chicago, 1987 — a bassline machine misused into a legend. Ride CUTOFF and PEAK while it runs.',
    bpm: 130, scale: 'minpent', gate: 0.55,
    mask: bits('1111111111111111'),
    slide: bits('0010001000100010'),
    accent: bits('1000100010001000'),
    deg: [0, 0, 0, 3, 0, 0, -2, 0, 0, 0, 0, 3, 0, 2, 0, 0],
    params: {
      cutoff: 420, res: 14, envAmt: 0.62, fdec: 0.22,
      a: 0.003, d: 0.14, s: 0.22, r: 0.07,
      penv: 0, ptime: 0.1, glide: 0.06, accentAmt: 0.7,
      drive: 0.35, unison: 2, detune: 9, sub: 1,
    },
  },
  psy: {
    label: 'PSY',
    note: 'Rolling triplet-feel offbeats, short gate, deep filter kick. Goa at 4am.',
    bpm: 145, scale: 'phrygian', gate: 0.3,
    mask: bits('0111011101110111'),
    slide: bits('0000000000000000'),
    accent: bits('0100010001000100'),
    deg: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    params: {
      cutoff: 300, res: 11, envAmt: 0.9, fdec: 0.1,
      a: 0.001, d: 0.09, s: 0.12, r: 0.05,
      penv: 0, ptime: 0.1, glide: 0, accentAmt: 0.35,
      drive: 0.5, unison: 3, detune: 14, sub: 2,
    },
  },
} as const satisfies Record<string, BassPatch>;

export type BassPatchId = keyof typeof BASS_PATCHES;
export const BASS_PATCH_IDS = Object.keys(BASS_PATCHES) as BassPatchId[];

/** Patches are frozen data; the live params object is a mutable copy of one. */
export const cloneParams = (p: BassParams): BassParams => ({ ...p });
